import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Heart, ShoppingCart, Star, Minus, Plus, MessageCircle, ChevronRight } from "lucide-react";
import api, { formatRp, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { fetchProductSEO } from "@/context/SEOContext";
import { useBrandName } from "@/context/BrandingContext";
import { useStorefrontConfig } from "@/context/StorefrontConfigContext";
import { resolveTrustBadges, iconComponentFor } from "@/lib/trustBadges";
import SEOHead from "@/components/SEOHead";
import ProductMediaGallery from "@/components/ProductMediaGallery";
import ProductReviews from "@/components/ProductReviews";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";
import { productJsonLd, breadcrumbJsonLd } from "@/lib/structuredData";
import ProductCard from "@/components/ProductCard";
import VariantSelector from "@/components/storefront/VariantSelector";
import {
  isVariantProduct,
  findMatchingVariant,
  getEffectiveVariantPrice,
  getVariantPriceRange,
  resolveVariantImage,
  clampQuantity,
} from "@/lib/variantSelection";
import { toast } from "sonner";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [settings, setSettings] = useState(null);
  const [productSeo, setProductSeo] = useState(null);
  const [variantSelection, setVariantSelection] = useState({});
  const { addToCart, toggleWishlist, isWishlisted } = useCart();
  const { user } = useAuth();
  const reviewsEnabled = useIsFeatureEnabled("reviews.product");
  const brandName = useBrandName();
  const { config: storefrontConfig } = useStorefrontConfig();
  const trustBadges = useMemo(
    () => resolveTrustBadges(storefrontConfig?.dna),
    [storefrontConfig?.dna],
  );
  const navigate = useNavigate();

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    setVariantSelection({}); // reset selection when navigating to a new product
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      if (r.data.category) {
        api.get("/products", { params: { category: r.data.category.slug, limit: 8 } })
          .then((rr) => setRelated(rr.data.items.filter((p) => p.id !== r.data.id)));
      }
    }).catch(() => setProduct(null));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    fetchProductSEO(slug).then(setProductSeo).catch(() => {});
  }, [slug]);

  // ==================== Sprint 3: Variant awareness (derived, hook-safe) ====================
  const isVariant = isVariantProduct(product);
  // Fail-closed UX: parent product is marked has_variants=true but the
  // public API did NOT return a `variants` array (feature flag OFF OR all
  // variants deleted/inactive). We must NOT expose parent price/stock/CTA
  // as a purchasable state. Backend already rejects any cart POST for such
  // a product; this branch just avoids a confusing UX.
  const variantsUnavailable = !!(
    product && product.has_variants && !isVariant
  );
  const selectedVariant = isVariant
    ? findMatchingVariant(product.variants, product.option_groups, variantSelection)
    : null;

  // Clamp qty against variant stock when selection changes (hook must run
  // unconditionally — kept above any early return).
  useEffect(() => {
    if (selectedVariant) {
      setQty((q) => clampQuantity(q, selectedVariant.stock));
    }
  }, [selectedVariant?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (product === null) {
    return <div className="max-w-7xl mx-auto p-12 text-center text-[#5C5C5C]">Memuat...</div>;
  }

  const priceRange = isVariant ? getVariantPriceRange(product.variants) : null;

  // Effective price / sale display
  let effectivePrice;
  let originalPrice;
  let hasDiscount;
  if (variantsUnavailable) {
    // Fail-closed: do NOT expose parent price as a sellable amount.
    // Leave effective price as null-ish so UI knows to show unavailable state.
    effectivePrice = null;
    originalPrice = null;
    hasDiscount = false;
  } else if (selectedVariant) {
    effectivePrice = getEffectiveVariantPrice(selectedVariant);
    originalPrice = selectedVariant.price;
    hasDiscount = !!(selectedVariant.sale_price != null && selectedVariant.sale_price < selectedVariant.price);
  } else if (isVariant && priceRange) {
    effectivePrice = priceRange.min;
    originalPrice = null;
    hasDiscount = false;
  } else {
    effectivePrice = product.sale_price || product.price;
    originalPrice = product.price;
    hasDiscount = !!product.sale_price && product.sale_price < product.price;
  }
  const discountPct = (hasDiscount && originalPrice)
    ? Math.round(((originalPrice - effectivePrice) / originalPrice) * 100)
    : 0;

  // Effective stock (parent for simple; variant for variant; null for unavailable)
  const effectiveStock = variantsUnavailable
    ? null
    : (selectedVariant
        ? Math.max(0, parseInt(selectedVariant.stock, 10) || 0)
        : (isVariant
            ? null // "select variant first" state
            : Math.max(0, parseInt(product.stock, 10) || 0)));

  // Effective SKU
  const effectiveSku = variantsUnavailable ? "-" : (selectedVariant?.sku || product.sku || "-");

  // Image switching based on variant image_index
  const variantImgIdx = selectedVariant
    ? resolveVariantImage(selectedVariant, product.images)
    : null;
  const displayedImgIdx = variantImgIdx !== null ? variantImgIdx : activeImg;

  const wished = isWishlisted(product.id);

  const handleBuyNow = async () => {
    if (isVariant && !selectedVariant) return;
    const ok = await addToCart(product.id, qty, selectedVariant?.id || null);
    if (ok) navigate("/cart");
  };
  const handleAddToCart = async () => {
    if (isVariant && !selectedVariant) return;
    await addToCart(product.id, qty, selectedVariant?.id || null);
  };

  const handleVariantSelect = (groupName, value) => {
    setVariantSelection((prev) => {
      const next = { ...prev, [groupName]: value };
      return next;
    });
  };

  const waMsg = encodeURIComponent(
    `Halo ${brandName || "Admin"}, saya tertarik dengan produk: ${product.name}` +
    (selectedVariant ? ` (${(selectedVariant.option_values || []).map((o) => `${o.group}: ${o.value}`).join(", ")})` : "") +
    ` (${formatRp(effectivePrice)}). Mohon info.`
  );
  const waUrl = settings?.whatsapp_number ? `https://wa.me/${settings.whatsapp_number}?text=${waMsg}` : null;

  // Structured data + SEO override for this product
  const productLd = productSeo ? productJsonLd(productSeo.product, productSeo._branding) : null;
  const breadcrumbLd = breadcrumbJsonLd([
    { name: "Beranda", url: "/" },
    { name: "Katalog", url: "/katalog" },
    ...(product.category ? [{ name: product.category.name, url: `/katalog?category=${product.category.slug}` }] : []),
    { name: product.name, url: `/produk/${product.slug || slug}` },
  ]);
  const seoJsonLd = [productLd, breadcrumbLd].filter(Boolean);

  // Sprint 4: variant products now PURCHASABLE. CTA disabled only when
  // selection incomplete OR selected variant is OOS.
  const requiresVariantSelection = isVariant && !selectedVariant;
  const variantOOS = !!selectedVariant && (Number(selectedVariant.stock) || 0) <= 0;
  const variantCTALabel = requiresVariantSelection
    ? "Pilih Varian"
    : (variantOOS ? "Stok Habis" : "+ Keranjang");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      {productSeo ? (
        <SEOHead seo={productSeo} jsonLd={seoJsonLd} />
      ) : (
        <SEOHead title={product.name} description={(product.description || "").slice(0, 160)} image={product.images?.[0]} jsonLd={seoJsonLd} />
      )}
      <nav className="text-xs text-[#5C5C5C] mb-4 flex items-center gap-1 flex-wrap" data-testid="breadcrumb">
        <Link to="/" className="hover:brand-text-primary">Beranda</Link>
        <ChevronRight size={12} />
        <Link to="/katalog" className="hover:brand-text-primary">Katalog</Link>
        {product.category && <>
          <ChevronRight size={12} />
          <Link to={`/katalog?category=${product.category.slug}`} className="hover:brand-text-primary">{product.category.name}</Link>
        </>}
      </nav>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
        {/* Gallery */}
        <ProductMediaGallery
          images={product.images || []}
          video={product.product_video}
          displayedImgIdx={displayedImgIdx}
          onSelectImage={setActiveImg}
          name={product.name}
          badge={hasDiscount ? (
            <span className="absolute top-4 left-4 commerce-sale-badge text-xs font-bold px-3 py-1.5 rounded-lg">
              -{discountPct}% OFF
            </span>
          ) : null}
        />

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            {product.is_bestseller && (
              <span className="color-accent-bg border text-[10px] font-bold px-2 py-1 rounded-lg uppercase" style={{ borderColor: "var(--color-accent)" }}>Terlaris</span>
            )}
            {product.is_featured && (
              <span className="bg-[#FFB703]/15 text-[#B5790F] text-[10px] font-bold px-2 py-1 rounded-lg uppercase">Unggulan</span>
            )}
            {isVariant && (
              <span className="bg-[#E85D04]/10 text-[#E85D04] text-[10px] font-bold px-2 py-1 rounded-lg uppercase" data-testid="variant-product-badge">
                Multi-varian
              </span>
            )}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight" data-testid="product-title">
            {product.name}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-[#5C5C5C]">
            {product.review_summary?.total_reviews > 0 && (
              <a href="#product-reviews" className="flex items-center gap-1" data-testid="product-rating-badge">
                <Star size={14} fill="#FFB703" stroke="#FFB703" />
                <span className="font-bold text-[#1A1A1A]">{product.review_summary.average_rating.toFixed(1)}</span>
                <span>({product.review_summary.total_reviews} ulasan)</span>
              </a>
            )}
            <span>•</span>
            <span>{product.sold_count || 0} terjual</span>
            <span>•</span>
            <span data-testid="product-sku">SKU: {effectiveSku}</span>
          </div>

          <div className="mt-5 p-5 brand-soft-bg rounded-2xl">
            <div className="flex items-baseline gap-3 flex-wrap">
              {variantsUnavailable ? (
                <div className="commerce-price font-display font-black text-2xl sm:text-3xl text-[#5C5C5C]" data-testid="product-price">
                  Belum tersedia
                </div>
              ) : isVariant && !selectedVariant && priceRange && priceRange.min !== priceRange.max ? (
                <>
                  <span className="text-[11px] uppercase tracking-widest font-bold text-[#5C5C5C]" data-testid="variant-price-label">Mulai dari</span>
                  <div className="commerce-price font-display font-black text-3xl sm:text-4xl" data-testid="product-price">
                    {formatRp(priceRange.min)}
                  </div>
                  <div className="text-[#5C5C5C] text-sm">— {formatRp(priceRange.max)}</div>
                </>
              ) : (
                <>
                  <div className="commerce-price font-display font-black text-3xl sm:text-4xl" data-testid="product-price">
                    {formatRp(effectivePrice)}
                  </div>
                  {hasDiscount && originalPrice && (
                    <div className="text-[#5C5C5C] line-through text-base" data-testid="product-original-price">{formatRp(originalPrice)}</div>
                  )}
                </>
              )}
              <div className="ml-auto text-xs uppercase font-bold text-[#5C5C5C]">per {(product.unit || "pcs").toUpperCase()}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-3" data-testid="product-stock-card">
              <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold">Stok</div>
              {variantsUnavailable ? (
                <div className="font-semibold text-[#5C5C5C] text-sm italic" data-testid="product-stock-value">
                  Varian sedang tidak aktif
                </div>
              ) : effectiveStock === null ? (
                <div className="font-semibold text-[#5C5C5C] text-sm italic" data-testid="product-stock-value">
                  Pilih varian untuk melihat stok
                </div>
              ) : effectiveStock <= 0 ? (
                <div className="font-bold text-base text-[#DC2F02]" data-testid="product-stock-value">Stok habis</div>
              ) : (
                <div className="font-bold text-base" data-testid="product-stock-value">{effectiveStock} {(product.unit || "pcs").toUpperCase()}</div>
              )}
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold">Berat</div>
              <div className="font-bold text-base">{((selectedVariant?.weight_grams ?? product.weight_grams) / 1000).toFixed(1)} kg</div>
            </div>
          </div>

          {/* Variant selector (Sprint 3 — read-only selection, no cart wiring) */}
          {isVariant && (
            <VariantSelector
              optionGroups={product.option_groups}
              variants={product.variants}
              selection={variantSelection}
              onSelect={handleVariantSelect}
            />
          )}

          <div className="mt-5">
            <div className="text-xs uppercase tracking-widest brand-text-primary font-bold mb-2">Jumlah</div>
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-white border border-[#E5E5E5] rounded-xl">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 grid place-items-center hover:bg-[#FAF9F6]" data-testid="qty-minus">
                  <Minus size={14} />
                </button>
                <div className="w-12 text-center font-bold" data-testid="qty-display">{qty}</div>
                <button
                  onClick={() => {
                    const cap = selectedVariant ? selectedVariant.stock : (isVariant ? 1 : product.stock);
                    setQty(Math.min(cap || 1, qty + 1));
                  }}
                  className="w-10 h-10 grid place-items-center hover:bg-[#FAF9F6]"
                  data-testid="qty-plus"
                  disabled={isVariant && !selectedVariant}
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="text-sm text-[#5C5C5C]">Subtotal: <span className="font-bold text-[#1A1A1A]">{formatRp(effectivePrice * qty)}</span></div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {variantsUnavailable ? (
              // Fail-closed: variant product but feature OFF / no active variants.
              // Never expose parent Add-to-Cart. Backend also rejects.
              <Button
                type="button"
                disabled
                className="flex-1 h-12 rounded-xl font-bold cursor-not-allowed opacity-70 bg-[#E5E5E5] text-[#5C5C5C] hover:bg-[#E5E5E5]"
                data-testid="variant-unavailable-cta"
                aria-disabled="true"
              >
                Varian produk sedang tidak tersedia
              </Button>
            ) : isVariant ? (
              // Sprint 4: variant products purchasable via variant_id.
              <>
                <Button
                  onClick={handleAddToCart}
                  disabled={requiresVariantSelection || variantOOS}
                  variant="outline"
                  className="flex-1 h-12 rounded-xl action-btn-secondary font-bold"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart size={16} className="mr-1" /> {variantCTALabel}
                </Button>
                <Button
                  onClick={handleBuyNow}
                  disabled={requiresVariantSelection || variantOOS}
                  className="flex-1 h-12 rounded-xl action-btn-primary font-bold"
                  data-testid="buy-now-btn"
                >
                  {requiresVariantSelection ? "Pilih Varian" : (variantOOS ? "Stok Habis" : "Beli Sekarang")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleAddToCart}
                  disabled={product.stock <= 0}
                  variant="outline"
                  className="flex-1 h-12 rounded-xl action-btn-secondary font-bold"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart size={16} className="mr-1" /> + Keranjang
                </Button>
                <Button
                  onClick={handleBuyNow}
                  disabled={product.stock <= 0}
                  className="flex-1 h-12 rounded-xl action-btn-primary font-bold"
                  data-testid="buy-now-btn"
                >
                  Beli Sekarang
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={() => toggleWishlist(product.id)}
              className={`h-12 w-12 rounded-xl ${wished ? "brand-bg-primary brand-border-primary text-white hover:opacity-90 hover:text-white" : ""}`}
              data-testid="wishlist-btn"
            >
              <Heart size={16} fill={wished ? "white" : "none"} />
            </Button>
          </div>

          {isVariant && (
            <div
              className="mt-2 text-[11px] text-[#5C5C5C] italic"
              data-testid="variant-cta-helper"
            >
              {requiresVariantSelection
                ? "Pilih semua opsi untuk melanjutkan pembelian."
                : (variantOOS
                    ? "Varian ini sedang habis. Silakan pilih varian lain."
                    : `SKU dipilih: ${selectedVariant.sku || "-"}`)}
            </div>
          )}

          {waUrl && (
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="wa-order-btn"
              className="mt-3 flex items-center justify-center gap-2 h-12 rounded-xl bg-[#25D366] hover:bg-[#1ea952] text-white font-bold transition-colors">
              <MessageCircle size={16} /> Order via WhatsApp
            </a>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3 text-xs" data-testid="product-trust-badges">
            {trustBadges.map((badge, index) => {
              const Icon = iconComponentFor(badge.icon);
              return (
                <div
                  key={`trust-${index}-${badge.text}`}
                  className="flex items-center gap-2 text-[#5C5C5C]"
                  data-testid={`trust-badge-${index}`}
                >
                  <Icon size={16} className="brand-text-primary" /> {badge.text}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Description */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-bold mb-3">Deskripsi Produk</h2>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 sm:p-6 text-[#1A1A1A] leading-relaxed whitespace-pre-wrap">
          {product.description || "Belum ada deskripsi."}
        </div>
      </section>

      {/* Reviews (feature-gated: reviews.product) */}
      {reviewsEnabled && <ProductReviews productId={product.id} initialSummary={product.review_summary} />}

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-3">Produk Terkait</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {related.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
